/**
 * EnquiryIntake — calm WhatsApp-style enquiry intake page.
 *
 * Stage-2 P4 (Feb 18, 2026). Doctrine:
 *   • Intake ONLY. Never sends, replies, or chats.
 *   • Future hooks (`external_channel`, `consent_received` etc.)
 *     exist on the form but trigger NO automation in v1.
 *   • Instructors see own enquiries; admin sees all.
 *   • Calm slate palette. No red, no urgency wording.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  ArrowLeft, Plus, Inbox, Clock, MessageSquare, Archive,
  Link2, CheckCheck, RefreshCw,
} from "lucide-react";
import { Link, useNavigate } from "react-router-dom";

const API_URL = process.env.REACT_APP_BACKEND_URL;


const EMPTY_VOCAB = {
  enquiry_types:    [],
  learner_statuses: [],
  statuses:         [],
  channels:         [],
  follow_up_buckets:[],
};


const STATUS_TONE = {
  new:           "bg-slate-200 text-slate-800",
  contacted:     "bg-indigo-100 text-indigo-900",
  waiting_reply: "bg-amber-50 text-amber-900",
  booked:        "bg-emerald-100 text-emerald-900",
  not_suitable:  "bg-slate-100 text-slate-500",
  archived:      "bg-slate-50 text-slate-400",
};


const BUCKET_ICON = {
  follow_up_today: Clock,
  waiting_reply:   MessageSquare,
  ready_to_invite: Inbox,
};


function fmtDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (!Number.isNaN(d.valueOf())) return d.toLocaleDateString();
    return iso;
  } catch { return iso; }
}


export default function EnquiryIntake() {
  const navigate = useNavigate();
  const [vocab, setVocab] = useState(EMPTY_VOCAB);
  const [items, setItems] = useState([]);
  const [scope, setScope] = useState("mine");
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState("all");
  const [composerOpen, setComposerOpen] = useState(false);

  const fetchVocab = useCallback(async () => {
    try {
      const r = await axios.get(
        `${API_URL}/api/enquiry-intake/vocabulary`,
        { withCredentials: true },
      );
      setVocab(r.data);
    } catch { /* silent */ }
  }, []);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [list, surf] = await Promise.all([
        axios.get(`${API_URL}/api/enquiry-intake`, { withCredentials: true }),
        axios.get(
          `${API_URL}/api/enquiry-intake/follow-ups/today`,
          { withCredentials: true },
        ),
      ]);
      setItems(list.data?.items || []);
      setScope(list.data?.scope || "mine");
      setGroups(surf.data?.groups || []);
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not load enquiries.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchVocab(); }, [fetchVocab]);
  useEffect(() => { fetchAll();   }, [fetchAll]);

  const filtered = useMemo(() => {
    if (filter === "all") return items;
    return items.filter((r) => r.status === filter);
  }, [items, filter]);

  return (
    <div data-testid="enquiry-intake-page" className="min-h-screen bg-slate-50 pb-24">
      {/* Sticky header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center gap-3">
          <button
            type="button"
            onClick={() => navigate(-1)}
            data-testid="enquiry-intake-back"
            className="p-2 -ml-2 rounded-md hover:bg-slate-100 text-slate-500"
            aria-label="Back"
          >
            <ArrowLeft className="w-4 h-4" />
          </button>
          <div className="flex-1 min-w-0">
            <h1
              data-testid="enquiry-intake-title"
              className="text-base font-bold text-slate-900 tracking-tight"
            >
              Enquiry Intake
            </h1>
            <p className="text-[11px] text-slate-400 mt-0.5">
              Capture WhatsApp & off-platform enquiries · {scope === "all" ? "all instructors" : "your enquiries"}
            </p>
          </div>
          <button
            type="button"
            onClick={fetchAll}
            data-testid="enquiry-intake-refresh"
            className="p-2 rounded-md hover:bg-slate-100 text-slate-500"
            aria-label="Refresh"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          </button>
          <button
            type="button"
            onClick={() => setComposerOpen(true)}
            data-testid="enquiry-intake-new"
            className="inline-flex items-center gap-1.5 bg-slate-900 text-white text-xs font-semibold rounded-md px-3 py-2 hover:bg-slate-800"
          >
            <Plus className="w-3.5 h-3.5" />
            New
          </button>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 pt-4 space-y-6">
        {/* Doctrine line — calm */}
        <p
          data-testid="enquiry-intake-doctrine"
          className="text-[11px] text-slate-400 italic"
        >
          The platform never replies for you. Capture what you saw,
          decide what to do.
        </p>

        {/* Calm follow-up surface */}
        <section data-testid="enquiry-intake-surface" className="space-y-3">
          <h2 className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold">
            Today's gentle list
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {groups.map((g) => {
              const Icon = BUCKET_ICON[g.key] || Inbox;
              return (
                <div
                  key={g.key}
                  data-testid={`enquiry-intake-bucket-${g.key}`}
                  data-bucket-count={String(g.count)}
                  className="bg-white border border-slate-200 rounded-xl p-3"
                >
                  <div className="flex items-center gap-2 mb-2">
                    <Icon className="w-4 h-4 text-slate-400" />
                    <span className="text-[12px] font-semibold text-slate-700">
                      {g.label}
                    </span>
                    <span className="ml-auto text-[11px] text-slate-400">
                      {g.count}
                    </span>
                  </div>
                  {g.items.length === 0 ? (
                    <p className="text-[12px] text-slate-400">
                      Nothing in this list. Calm day.
                    </p>
                  ) : (
                    <ul className="space-y-2">
                      {g.items.slice(0, 4).map((it) => (
                        <li key={it.id} className="text-[12px] truncate">
                          <span className="font-medium text-slate-800">{it.name}</span>
                          {it.preferred_area_postcode ? (
                            <span className="text-slate-400"> · {it.preferred_area_postcode}</span>
                          ) : null}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              );
            })}
          </div>
        </section>

        {/* Filter chips */}
        <section className="flex flex-wrap gap-2">
          <FilterChip
            label="All" value="all" active={filter === "all"}
            onClick={setFilter}
          />
          {vocab.statuses.map((s) => (
            <FilterChip
              key={s.key} label={s.label} value={s.key}
              active={filter === s.key} onClick={setFilter}
            />
          ))}
        </section>

        {/* Error banner */}
        {error ? (
          <p
            data-testid="enquiry-intake-error"
            className="text-[12px] text-slate-500 bg-slate-100 border border-slate-200 rounded-md px-3 py-2"
          >
            {String(error)}
          </p>
        ) : null}

        {/* List */}
        <section data-testid="enquiry-intake-list" className="space-y-2">
          {filtered.length === 0 && !loading ? (
            <div
              data-testid="enquiry-intake-empty"
              className="bg-white border border-dashed border-slate-200 rounded-xl p-6 text-center"
            >
              <p className="text-sm text-slate-500">
                No enquiries here yet. When a WhatsApp message comes in,
                tap <span className="font-semibold">New</span> to capture it.
              </p>
            </div>
          ) : null}

          {filtered.map((row) => (
            <EnquiryRow
              key={row.id}
              row={row}
              vocab={vocab}
              onRefresh={fetchAll}
            />
          ))}
        </section>
      </main>

      {composerOpen ? (
        <ComposerDrawer
          vocab={vocab}
          onClose={() => setComposerOpen(false)}
          onCreated={() => { setComposerOpen(false); fetchAll(); }}
        />
      ) : null}

      <Link
        to="/instructor"
        data-testid="enquiry-intake-home-link"
        className="hidden"
        aria-hidden="true"
      >home</Link>
    </div>
  );
}


function FilterChip({ label, value, active, onClick }) {
  return (
    <button
      type="button"
      onClick={() => onClick(value)}
      data-testid={`enquiry-intake-filter-${value}`}
      className={`text-[11px] uppercase tracking-wide font-semibold px-3 py-1.5 rounded-full border ${
        active
          ? "bg-slate-900 text-white border-slate-900"
          : "bg-white text-slate-600 border-slate-200 hover:bg-slate-100"
      }`}
    >
      {label}
    </button>
  );
}


function EnquiryRow({ row, vocab, onRefresh }) {
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const [feedback, setFeedback] = useState(null);

  const setStatus = async (toStatus) => {
    setBusy(true);
    setFeedback(null);
    try {
      await axios.post(
        `${API_URL}/api/enquiry-intake/${row.id}/status`,
        { to_status: toStatus },
        { withCredentials: true },
      );
      onRefresh();
    } catch (err) {
      setFeedback(err?.response?.data?.detail?.message || "Could not update.");
    } finally {
      setBusy(false);
    }
  };

  const invite = async () => {
    setBusy(true);
    setFeedback(null);
    try {
      const r = await axios.post(
        `${API_URL}/api/enquiry-intake/${row.id}/invite`,
        {},
        { withCredentials: true },
      );
      setFeedback(`Invite code: ${r.data.invite_code}`);
      onRefresh();
    } catch (err) {
      const d = err?.response?.data?.detail;
      setFeedback(d?.code === "already_linked" ? "Already linked." : "Could not invite.");
    } finally {
      setBusy(false);
    }
  };

  const typeLabel = useMemo(
    () => vocab.enquiry_types.find((t) => t.key === row.enquiry_type)?.label,
    [vocab, row.enquiry_type],
  );

  return (
    <div
      data-testid={`enquiry-row-${row.id}`}
      data-status={row.status}
      data-bucket={row.follow_up_bucket || "none"}
      className="bg-white border border-slate-200 rounded-xl p-3"
    >
      <div className="flex items-start gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-slate-900 text-sm truncate">
              {row.name}
            </span>
            <span
              data-testid={`enquiry-row-status-${row.id}`}
              className={`text-[10px] uppercase tracking-wide font-semibold px-1.5 py-0.5 rounded ${STATUS_TONE[row.status] || "bg-slate-100 text-slate-700"}`}
            >
              {row.status.replace("_", " ")}
            </span>
            {typeLabel ? (
              <span className="text-[10px] text-slate-400">· {typeLabel}</span>
            ) : null}
            {row.external_channel ? (
              <span className="text-[10px] text-slate-400">· {row.external_channel}</span>
            ) : null}
          </div>
          <div className="text-[12px] text-slate-500 mt-1 space-x-2">
            {row.phone ? <span>{row.phone}</span> : null}
            {row.preferred_area_postcode ? <span>· {row.preferred_area_postcode}</span> : null}
            {row.next_follow_up_date ? (
              <span data-testid={`enquiry-row-followup-${row.id}`}>
                · follow up {fmtDate(row.next_follow_up_date)}
              </span>
            ) : null}
          </div>
          {row.message_summary ? (
            <p className="text-[12px] text-slate-600 mt-1.5 line-clamp-2">
              {row.message_summary}
            </p>
          ) : null}
          {row.linked_student_id ? (
            <p className="text-[11px] text-emerald-700 mt-1">
              Linked to learner record
            </p>
          ) : null}
        </div>
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          data-testid={`enquiry-row-toggle-${row.id}`}
          className="text-[11px] text-slate-500 px-2 py-1 rounded hover:bg-slate-100"
        >
          {open ? "Close" : "Open"}
        </button>
      </div>

      {open ? (
        <div className="mt-3 pt-3 border-t border-slate-100 space-y-2">
          <div className="flex flex-wrap gap-2">
            {row.status === "new" || row.status === "waiting_reply" ? (
              <ActionButton
                testid={`enquiry-row-contacted-${row.id}`}
                onClick={() => setStatus("contacted")} busy={busy}
                Icon={CheckCheck} label="Mark contacted"
              />
            ) : null}
            {row.status === "contacted" ? (
              <ActionButton
                testid={`enquiry-row-waiting-${row.id}`}
                onClick={() => setStatus("waiting_reply")} busy={busy}
                Icon={MessageSquare} label="Waiting reply"
              />
            ) : null}
            {(row.status === "contacted" || row.status === "waiting_reply") ? (
              <ActionButton
                testid={`enquiry-row-booked-${row.id}`}
                onClick={() => setStatus("booked")} busy={busy}
                Icon={CheckCheck} label="Mark booked"
              />
            ) : null}
            {!row.linked_student_id && row.status !== "archived" ? (
              <ActionButton
                testid={`enquiry-row-invite-${row.id}`}
                onClick={invite} busy={busy}
                Icon={Link2} label="Create learner invite"
              />
            ) : null}
            {row.status !== "archived" ? (
              <ActionButton
                testid={`enquiry-row-archive-${row.id}`}
                onClick={() => setStatus("archived")} busy={busy}
                Icon={Archive} label="Archive"
              />
            ) : null}
          </div>
          {feedback ? (
            <p
              data-testid={`enquiry-row-feedback-${row.id}`}
              className="text-[11px] text-slate-500"
            >
              {feedback}
            </p>
          ) : null}
          {row.notes ? (
            <p className="text-[12px] text-slate-600">
              <span className="text-slate-400">Notes: </span>
              {row.notes}
            </p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}


function ActionButton({ testid, onClick, busy, Icon, label }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={busy}
      data-testid={testid}
      className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-slate-700 bg-slate-100 hover:bg-slate-200 disabled:opacity-50 disabled:cursor-not-allowed px-2.5 py-1.5 rounded-md"
    >
      <Icon className="w-3 h-3" />
      {label}
    </button>
  );
}


function ComposerDrawer({ vocab, onClose, onCreated }) {
  const [form, setForm] = useState({
    name: "", phone: "", email: "", message_summary: "",
    enquiry_type: "", preferred_area_postcode: "", learner_status: "",
    notes: "", next_follow_up_date: "",
    external_channel: "whatsapp",
    source_message_id: "",
    consent_received: false,
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const payload = {};
      for (const [k, v] of Object.entries(form)) {
        if (v === "" || v === null) continue;
        payload[k] = v;
      }
      await axios.post(
        `${API_URL}/api/enquiry-intake`,
        payload,
        { withCredentials: true },
      );
      onCreated();
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not save.");
    } finally {
      setBusy(false);
    }
  };

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <div
      data-testid="enquiry-intake-composer"
      className="fixed inset-0 z-30 bg-black/40 flex items-stretch justify-end"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md bg-white h-full overflow-y-auto"
      >
        <form onSubmit={submit} className="p-4 space-y-3">
          <h3 className="text-base font-bold text-slate-900">New enquiry</h3>
          <p className="text-[11px] text-slate-400">
            Capture what you saw on WhatsApp or another channel. The
            platform doesn't send anything — that's still your call.
          </p>

          <Field label="Name *">
            <input
              required
              type="text"
              data-testid="composer-name"
              value={form.name}
              onChange={(e) => setField("name", e.target.value)}
              className="w-full text-sm border border-slate-200 rounded-md px-2 py-1.5"
            />
          </Field>

          <div className="grid grid-cols-2 gap-2">
            <Field label="Phone">
              <input
                type="tel"
                data-testid="composer-phone"
                value={form.phone}
                onChange={(e) => setField("phone", e.target.value)}
                className="w-full text-sm border border-slate-200 rounded-md px-2 py-1.5"
              />
            </Field>
            <Field label="Postcode">
              <input
                type="text"
                data-testid="composer-postcode"
                value={form.preferred_area_postcode}
                onChange={(e) => setField("preferred_area_postcode", e.target.value)}
                className="w-full text-sm border border-slate-200 rounded-md px-2 py-1.5"
              />
            </Field>
          </div>

          <Field label="Message summary">
            <textarea
              rows={3}
              data-testid="composer-summary"
              value={form.message_summary}
              onChange={(e) => setField("message_summary", e.target.value)}
              className="w-full text-sm border border-slate-200 rounded-md px-2 py-1.5"
              placeholder="What they wrote on WhatsApp."
            />
          </Field>

          <div className="grid grid-cols-2 gap-2">
            <Field label="Enquiry type">
              <select
                data-testid="composer-type"
                value={form.enquiry_type}
                onChange={(e) => setField("enquiry_type", e.target.value)}
                className="w-full text-sm border border-slate-200 rounded-md px-2 py-1.5"
              >
                <option value="">—</option>
                {vocab.enquiry_types.map((t) => (
                  <option key={t.key} value={t.key}>{t.label}</option>
                ))}
              </select>
            </Field>
            <Field label="Learner status">
              <select
                data-testid="composer-learner-status"
                value={form.learner_status}
                onChange={(e) => setField("learner_status", e.target.value)}
                className="w-full text-sm border border-slate-200 rounded-md px-2 py-1.5"
              >
                <option value="">—</option>
                {vocab.learner_statuses.map((t) => (
                  <option key={t.key} value={t.key}>{t.label}</option>
                ))}
              </select>
            </Field>
          </div>

          <Field label="Next follow-up date">
            <input
              type="date"
              data-testid="composer-followup"
              value={form.next_follow_up_date}
              onChange={(e) => setField("next_follow_up_date", e.target.value)}
              className="w-full text-sm border border-slate-200 rounded-md px-2 py-1.5"
            />
          </Field>

          <Field label="Notes">
            <textarea
              rows={2}
              data-testid="composer-notes"
              value={form.notes}
              onChange={(e) => setField("notes", e.target.value)}
              className="w-full text-sm border border-slate-200 rounded-md px-2 py-1.5"
            />
          </Field>

          <details className="text-[11px] text-slate-500">
            <summary className="cursor-pointer">Channel hooks (optional)</summary>
            <div className="grid grid-cols-1 gap-2 mt-2">
              <Field label="Channel">
                <select
                  data-testid="composer-channel"
                  value={form.external_channel}
                  onChange={(e) => setField("external_channel", e.target.value)}
                  className="w-full text-sm border border-slate-200 rounded-md px-2 py-1.5"
                >
                  {vocab.channels.map((c) => (
                    <option key={c.key} value={c.key}>{c.label}</option>
                  ))}
                </select>
              </Field>
              <Field label="Source message ID">
                <input
                  type="text"
                  data-testid="composer-source-id"
                  value={form.source_message_id}
                  onChange={(e) => setField("source_message_id", e.target.value)}
                  className="w-full text-sm border border-slate-200 rounded-md px-2 py-1.5"
                  placeholder="Paste from WhatsApp Web URL"
                />
              </Field>
              <label className="inline-flex items-center gap-2">
                <input
                  type="checkbox"
                  data-testid="composer-consent"
                  checked={form.consent_received}
                  onChange={(e) => setField("consent_received", e.target.checked)}
                />
                <span>Consent received to keep contact details on file</span>
              </label>
            </div>
          </details>

          {error ? (
            <p
              data-testid="composer-error"
              className="text-[11px] text-slate-500 bg-slate-100 border border-slate-200 rounded px-2 py-1.5"
            >
              {String(error)}
            </p>
          ) : null}

          <div className="flex items-center gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              data-testid="composer-cancel"
              className="flex-1 text-sm font-semibold text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-md px-3 py-2"
            >
              Cancel
            </button>
            <button
              type="submit"
              data-testid="composer-save"
              disabled={busy || !form.name.trim()}
              className="flex-1 text-sm font-semibold text-white bg-slate-900 hover:bg-slate-800 disabled:opacity-50 rounded-md px-3 py-2"
            >
              {busy ? "Saving…" : "Save enquiry"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}


function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-[11px] uppercase tracking-wide text-slate-500 font-semibold mb-1">
        {label}
      </span>
      {children}
    </label>
  );
}
